import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormHelperText from '@mui/material/FormHelperText';
import MUIRadioGroup from '@mui/material/RadioGroup';
import React, { memo, useCallback } from 'react';

import FormLabel from '~/common/components/FormLabel';
import { FORM_MESSAGES } from '~/common/constants';
import { useFieldContext } from '~/common/form/formContexts';
import { StyledAdditionalNotes } from '~/common/mui/Radios/MUIRadioGroup/StyledRadioElements.js';
import { RADIO_OPTIONS_ARRAY_YES_NO } from '~/common/mui/Radios/MUIRadioGroup/constants';

import Radio from './Radio';

type RadioOption = {
  label: React.ReactNode;
  value: string;
  disabled?: boolean;
  ariaLabel?: string;
};

type RadioGroupProps = {
  id?: string;
  label: React.ReactNode;
  isLabelHidden?: boolean;
  isCustomLabel?: boolean;
  name: string;
  options?: RadioOption[];
  disabled?: boolean;
  error?: boolean;
  errorMessage?: string;
  value?: string | null;
  onChange?: (...args: any[]) => any;
  onFocus?: (e: React.FocusEvent<HTMLDivElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLDivElement>) => void;
  passEvent?: boolean;
  row?: boolean; // Add the passEvent prop;
  tooltip?: string;
  additionalNotes?: string;
  additionalNotesStyles?: React.CSSProperties;
  dataTestid?: string;
  minWidth?: string;
  marginTop?: string;
  inputRef?: React.Ref<HTMLDivElement>;
  required?: boolean;
  sx?: React.CSSProperties;
  radioGroupSx?: React.CSSProperties;
  useTanstackForm?: boolean;
};

function TanstackFormRadioGroup(
  props: Readonly<
    Omit<
      RadioGroupProps,
      | 'useTanstackForm'
      | 'value'
      | 'onChange'
      | 'error'
      | 'errorMessage'
      | 'onBlur'
    >
  >,
) {
  const field = useFieldContext<string>();
  const errorMessage = field.state.meta.errors[0] as string | undefined;
  return (
    <BaseRadioGroup
      {...props}
      value={field.state.value ?? null}
      onChange={(val: string) => field.handleChange(val)}
      onBlur={() => field.handleBlur()}
      error={field.state.meta.errors.length > 0}
      errorMessage={errorMessage}
    />
  );
}

/**
 * RadioGroup Component
 *
 * Props:
 * - name: string (required) - The name of the radio group.
 * - label: string (required) - The label for the radio group.
 * - options: array (required) - The options for the radio group.
 * - value: string (required) - The selected value of the radio group.
 * - onChange: function (required) - The function to call when the value changes.
 * - passEvent: bool - Whether to pass the event object to the onChange handler.
 * - row: bool - Whether to display the radio buttons in a row.
 * - error: bool or object - Whether there is an error.
 * - errorMessage: string - The error message to display.
 * - field: object - Additional field props.
 * - tooltip: string - The tooltip to display.
 * - additionalNotes: string - Additional notes to display.
 * - additionalNotesStyles: object - Custom styles for the additional notes.
 * - disabled: bool - Whether the radio group is disabled.
 * - dataTestid: string - The data-testid attribute for the radio group.
 *
 * Example usage:
 * <RadioGroup
 *   name="example"
 *   label="Example Radio Group"
 *   options={[
 *     { value: 'option1', label: 'Option 1' },
 *     { value: 'option2', label: 'Option 2' },
 *   ]}
 *   value="option1"
 *   onChange={handleChange}
 *   additionalNotes="Some additional notes"
 *   additionalNotesStyles={{
 *     fontWeight: 'bold',
 *     fontStyle: 'italic',
 *     padding: '10px',
 *   }}
 *  minWidth="141px"
 *  inputRef={register('example')}
 * />
 */
const RadioGroup = ({
  useTanstackForm = false,
  ...props
}: Readonly<RadioGroupProps>) => {
  if (useTanstackForm) {
    return <TanstackFormRadioGroup {...props} />;
  }
  return <BaseRadioGroup {...props} />;
};

const BaseRadioGroup = ({
  id,
  error,
  label,
  isLabelHidden = false,
  name,
  isCustomLabel = false,
  options = RADIO_OPTIONS_ARRAY_YES_NO,
  value = null,
  row = true,
  errorMessage,
  onChange,
  onFocus,
  onBlur,
  passEvent,
  tooltip,
  additionalNotes,
  additionalNotesStyles,
  disabled,
  minWidth,
  dataTestid,
  marginTop,
  inputRef,
  required = false,
  radioGroupSx,
}: Readonly<RadioGroupProps>) => {
  const helperText = error ? errorMessage || FORM_MESSAGES.selectAnOption : '';

  const handleChange = useCallback(
    (event) => {
      onChange(passEvent ? event : event.target.value);
    },
    [onChange, passEvent],
  );

  return (
    <FormControl
      error={error}
      component="fieldset"
      sx={{
        minWidth: minWidth || 'auto',
        width: '100%',
        marginTop: marginTop || '1.25rem',
      }}
      id={id}
      required={required}
    >
      {isCustomLabel
        ? label
        : !isLabelHidden && <FormLabel label={label} tooltip={tooltip} />}

      {!!additionalNotes && (
        <StyledAdditionalNotes
          className="Mui-additional-notes"
          {...additionalNotesStyles}
        >
          {additionalNotes}
        </StyledAdditionalNotes>
      )}
      <MUIRadioGroup
        name={name}
        value={value}
        row={row}
        onChange={handleChange}
        onFocus={onFocus}
        onBlur={onBlur}
        data-testid={dataTestid}
        {...(isLabelHidden &&
          typeof label === 'string' && { 'aria-label': label })}
        sx={{ marginBottom: 'unset', ...radioGroupSx }}
        ref={inputRef}
      >
        {options.map((option) => (
          <FormControlLabel
            key={option.value}
            value={option.value}
            label={option.label}
            sx={{ marginRight: '1.5rem' }}
            aria-invalid={error}
            aria-label={option.ariaLabel}
            {...(error && { 'aria-errormessage': `${name}-error ${error}` })}
            disabled={disabled || option.disabled}
            control={
              <Radio data-testid={`${name}-${option.value}`} error={error} />
            }
          />
        ))}
      </MUIRadioGroup>
      {helperText && (
        <FormHelperText
          id={`${name}-error`}
          sx={{ fontSize: '0.875rem', marginLeft: '0' }}
        >
          {helperText}
        </FormHelperText>
      )}
    </FormControl>
  );
};

export default memo(RadioGroup);
